// Calm display over a weak link (Dominik, 19.10.2026: "mir ist wichtig, dass die UI stabil ausschaut und nicht wegen
// verbindungsabbrüchen ständig schwankt"). Short gaps change nothing on screen: the last values stay (dimmed, with
// their age), controls keep their look, no bar or banner appears. Only a gap that lasts, or a machine the API reports
// offline, changes the screen. Pure functions of the mirror and this device's clock; the thresholds and their reasons
// are documented in cloud/apps/app/UI-REVIEW.md ("Ruhige Anzeige").
import type { NetState } from './connection.js';
import type { MachineView } from './store.js';

/**
 * Live values come about once a second (firmware CloudLink liveIntervalMs, QoS 0, only under the watch lease). On the
 * machine's weak Wi-Fi (25-57 % loss, pings 0.1-5 s) TCP retransmissions hold them back for several seconds, and a full
 * outbox drops some. A gap up to 15 s is a short gap (the same 15 s as the API's "verbindet neu", FR-14/AD-19): the
 * value still counts as current.
 */
export const LIVE_QUIET_MS = 15_000;
/**
 * After a short gap the last value stays, dimmed and with its age, up to 60 s: by then the API has marked a silent
 * machine offline (keepalive 30 s x 1.5 + 15 s, FR-14) and the 60-s watch lease has run out. After 60 s the screen
 * says "Keine Live-Werte".
 */
export const LIVE_HOLD_MS = 60_000;
/**
 * The API reports a dropped machine as "reconnecting" for up to 15 s before "offline" (AD-19). The first 10 s of it
 * look like online (most reconnects finish sooner); the last 5 s show "verbindet neu" in the header chip only.
 */
export const MACHINE_QUIET_MS = 10_000;
/**
 * This device's WebSocket reconnects with a backoff of 0.5, 1, 2, 4 s (core/connection.ts): a gap of this device shows
 * (cloud bar, read-only note, locked controls) only after 10 s. Commands tapped meanwhile get a clear answer at once
 * (core/commands.ts: not sent, no connection).
 */
export const LINK_QUIET_MS = 10_000;

/**
 * What the screen says about the machine's connection:
 * - online: online, or a drop of the machine or of this device that is still short
 * - reconnecting: the machine has been reconnecting for 10 s or more (header chip only)
 * - restarting: a planned restart (update, "Jetzt neu starten")
 * - offline: the API says so (FR-14: at most 60 s after the drop)
 * - unknown: the machine has not announced itself for 10 s or more (e.g. after a broker restart)
 * - link_down: this device has had no connection to the cloud for 10 s or more; the values are as of the drop
 */
export type Presence = 'online' | 'reconnecting' | 'restarting' | 'offline' | 'unknown' | 'link_down';

/** This device's clock for a time the API stamped. */
export const localTime = (view: MachineView, serverMs: number): number => serverMs - view.clockOffset;

/** The mirror holds data of the machine (a snapshot arrived at least once). */
export const hasData = (view: MachineView): boolean => view.info !== null;

export function presenceOf(view: MachineView, now: number): Presence {
  if (!hasData(view)) return 'unknown';
  if (view.linkLostAt !== null && now - view.linkLostAt >= LINK_QUIET_MS) return 'link_down';
  const c = view.conn;
  if (c.state === 'online') return 'online';
  if (c.state === 'reconnecting' || c.state === 'unknown') {
    const since = c.since > 0 ? localTime(view, c.since) : now;
    if (now - since < MACHINE_QUIET_MS) return 'online';
    return c.state;
  }
  return c.state;
}

/** The connection of this device is fine, or its gap is still short. */
export const linkCalm = (view: MachineView, now: number): boolean =>
  view.ready || (view.linkLostAt !== null && now - view.linkLostAt < LINK_QUIET_MS);

/**
 * Controls look usable: data there, this device's link calm, the machine online or reconnecting (the API answers
 * "Maschine offline" at once if a tap comes too early), no emergency stop. Whether a command may go out right now
 * decides the command sender (core/commands.ts); this only keeps short gaps off the screen.
 */
export function controlsUsable(view: MachineView, now: number): boolean {
  if (!hasData(view) || view.remoteLocked || !linkCalm(view, now)) return false;
  const p = presenceOf(view, now);
  return p === 'online' || p === 'reconnecting';
}

/**
 * - live: current (at most 15 s old, machine online)
 * - aging: the last value, older than 15 s or from a machine that is reconnecting: shown dimmed with its age
 * - none: no value, older than 60 s, or the machine is offline / restarting / unknown
 */
export type LiveLook = 'live' | 'aging' | 'none';

export function liveLook(view: MachineView, now: number): { look: LiveLook; ageMs: number | null } {
  if (!view.live) return { look: 'none', ageMs: null };
  const ageMs = Math.max(0, now - localTime(view, view.live.receivedAt));
  const p = presenceOf(view, now);
  if (ageMs > LIVE_HOLD_MS || p === 'offline' || p === 'restarting' || p === 'unknown')
    return { look: 'none', ageMs };
  if (ageMs <= LIVE_QUIET_MS && p === 'online') return { look: 'live', ageMs };
  return { look: 'aging', ageMs };
}

/**
 * The cloud bar of this device: a gap after a connection counts as connected for 10 s, then the bar shows; connecting
 * at the start is said (screen readers) but shows as a bar only once it takes 10 s.
 */
export function calmNet(net: NetState, sinceMs: number, now: number): { net: NetState; bar: boolean } {
  if (net === 'ok') return { net, bar: false };
  const lasting = now - sinceMs >= LINK_QUIET_MS;
  if (net === 'connecting') return { net, bar: lasting };
  return lasting ? { net, bar: true } : { net: 'ok', bar: false };
}

/** An age in calm steps: 5-s steps under a minute ("vor 20 s"), then minutes, then hours. */
export function calmAgeSeconds(ageMs: number): { n: number; unit: 's' | 'min' | 'h' } {
  const s = Math.max(0, Math.floor(ageMs / 1000));
  if (s < 60) return { n: Math.floor(s / 5) * 5, unit: 's' };
  const min = Math.floor(s / 60);
  if (min < 60) return { n: min, unit: 'min' };
  return { n: Math.floor(min / 60), unit: 'h' };
}
